import { useMemo } from 'react';
import {
  CareType,
  GetContractQuery,
  ListContractsItemModel,
  ListContractsQuery,
  useGetContractQuery,
  useListContractsQuery,
  useOrganizationCareFlowsQuery,
} from '../../../graphQL';
import { useOrganizationId } from '../../Organizations/util';
import { ApolloError } from '@apollo/client';

type UseContractsPageReturn =
  | { error: true; errorList: (ApolloError | undefined)[] }
  | { loading: true; error: false; data: undefined }
  | {
      loading: false;
      error: false;
      data: {
        orgContractsList: ListContractsQuery['listContracts']['contracts'];
        contractId: number | undefined;
        contractData: GetContractQuery | undefined;
        refetchContractData: () => void;
        refetchContractList: () => void;
        contractOrgHasParent: boolean;
        organization: {
          careTypes: CareType[];
        };
      };
    };

export const useContractsPage = (
  selectedContractId: number | undefined
): UseContractsPageReturn => {
  const organizationId = useOrganizationId();

  const {
    data: organizationData,
    error: organizationError,
    loading: organizationLoading,
  } = useOrganizationCareFlowsQuery({ variables: { organizationId } });

  const {
    data: contractListData,
    loading: contractListLoading,
    error: contractListError,
    refetch: refetchContractList,
  } = useListContractsQuery({ variables: { organizationId }, skip: !organizationId });

  const currentContractId = useMemo(() => {
    if (contractListData?.listContracts.contracts) {
      return determineCurrentContract(contractListData?.listContracts.contracts);
    }
    return undefined;
  }, [contractListData?.listContracts]);

  const activeContractId = selectedContractId ?? currentContractId;

  const {
    data: contractData,
    loading: contractLoading,
    error: contractError,
    refetch,
  } = useGetContractQuery({
    variables: { organizationId, contractId: activeContractId ?? 0 },
    skip: activeContractId === undefined,
  });

  const careTypes = useMemo(() => {
    const organizationCareTypes = organizationData?.organization.careFlows.map(
      flow => flow.careType
    );

    return organizationCareTypes ? [...new Set(organizationCareTypes)] : undefined;
  }, [organizationData?.organization.careFlows]);

  const loading = contractListLoading || contractLoading || organizationLoading;
  const hasError = !!contractListError || !!contractError || !!organizationError;

  if (hasError) {
    return {
      error: true,
      errorList: [contractListError, contractError, organizationError],
    };
  }
  if (loading) {
    return {
      loading: true,
      error: false,
      data: undefined,
    };
  }

  return {
    loading: false,
    error: false,
    data: {
      orgContractsList: contractListData?.listContracts.contracts ?? [],
      contractId: activeContractId,
      contractData: contractData,
      refetchContractData: refetch,
      refetchContractList,
      contractOrgHasParent: !!contractData?.organization.parent ?? false,
      organization: {
        careTypes: careTypes ?? [],
      },
    },
  };
};

/**
 * Pick the current contract, or the future contract closest to today
 * Note: this assumes item dates do not overlap
 */
function determineCurrentContract(contracts: ListContractsItemModel[]): number | undefined {
  if (!contracts.length) {
    return undefined;
  }
  const sortedContracts = contracts
    .slice()
    .sort(
      (contract1, contract2) =>
        new Date(contract1.startDate).getTime() - new Date(contract2.startDate).getTime()
    );
  const currentTime = new Date().getTime();
  const currentContract = sortedContracts.find(contract => {
    const startTime = new Date(contract.startDate).getTime();
    const endTime = new Date(contract.endDate).getTime();
    return startTime <= currentTime && endTime >= currentTime;
  });

  if (currentContract) {
    return currentContract.id;
  }

  // Else no current contract, select the contract in the future closest to today
  const futureContracts = sortedContracts.filter(
    contract => new Date(contract.startDate).getTime() > currentTime
  );
  if (futureContracts.length) {
    return futureContracts[0]?.id;
  }
  return undefined;
}
